/**
 * 五行姓名阶层判定（基于萧吉《五行大义》气-阴阳-五行宇宙论）
 *
 * 综合品级（100分制）= 德性分 × 40% + 音阶分 × 30% + 三才分 × 20% − 刑冲罚分（≤10%）
 * 五等阶位：上品(太和天极) / 中上品(经纬济世) / 中品(文质怀才) / 中下品(乘势进退) / 下品(孤渊未开)
 */

import type { Element } from "../shared/types";
import { ELEMENTS } from "./elements";

// =====================================================================
// ===                    维度一 · 五常德性（40%）                    ===
// =====================================================================

/** 五常对应：木仁、火礼、土信、金义、水智 */
const DE_VIRTUE: Record<Element, string> = {
  木: "仁",
  火: "礼",
  土: "信",
  金: "义",
  水: "智",
};

/**
 * 五常德性分：满 100
 *  - 五行俱全 + 土居中位：95-100
 *  - 五行俱全但土不在中：80-94
 *  - 缺一行：70
 *  - 缺两行：55
 *  - 缺三行：35
 *  - 缺四行：15
 *  - 犯"背德"（木盛克土）：基础分 −20
 */
function virtueScore(counts: Record<Element, number>): { score: number; note: string } {
  const present = ELEMENTS.filter((el) => counts[el] > 0);
  const presentCount = present.length;
  // 五行俱全
  if (presentCount === 5) {
    return { score: 92, note: "五行俱全，土信未居中位" };
  }
  if (presentCount === 4) {
    return { score: 70, note: `缺${ELEMENTS.find((el) => counts[el] === 0)!}行` };
  }
  if (presentCount === 3) {
    return { score: 55, note: `缺${ELEMENTS.filter((el) => counts[el] === 0).join("·")}行` };
  }
  if (presentCount === 2) {
    return { score: 35, note: `独行偏倚：缺${ELEMENTS.filter((el) => counts[el] === 0).join("·")}行` };
  }
  return { score: 15, note: `五行死寂：仅存${present[0] ?? "?"}行` };
}

// =====================================================================
// ===                    维度二 · 五音位阶（30%）                    ===
// =====================================================================

/**
 * 简化五音归类：按声母 + 韵部粗分
 *  - 宫(君)：舌根音，韵母含 a/ang/an/o → 最高
 *  - 商(臣)：齿音，韵母含 e/en/eng → 次高
 *  - 角(民)：牙音，韵母含 i/ie → 中
 *  - 徵(事)：舌尖音，韵母含 u/uo → 次末
 *  - 羽(物)：唇音，韵母含 ü/u → 末
 *
 * 此处按汉字拼音首字母粗略归类
 */
const SHENG_MU_TO_GONG: Record<string, "宫" | "商" | "角" | "徵" | "羽"> = {
  // 宫（君）：g k h
  g: "宫", k: "宫", h: "宫",
  // 商（臣）：zh ch sh r
  zh: "商", ch: "商", sh: "商", r: "商",
  // 角（民）：j q x
  j: "角", q: "角", x: "角",
  // 徵（事）：z c s
  z: "徵", c: "徵", s: "徵",
  // 羽（物）：b p m f d t n l
  b: "羽", p: "羽", m: "羽", f: "羽", d: "羽", t: "羽", n: "羽", l: "羽",
  // y w 视上下文
  y: "角", w: "宫",
  // 零声母
  "": "宫",
};

const GONG_RANK: Record<"宫" | "商" | "角" | "徵" | "羽", number> = {
  宫: 100,
  商: 85,
  角: 70,
  徵: 55,
  羽: 40,
};

/** 取汉字的拼音首字母（用 chars.json 查） */
import charsData from "../data/chars.json";

const CHARS_DB: Record<string, { pinyin: string; strokes: number }> = (charsData as any).chars ?? {};

function getPinyinFirstChar(char: string): string {
  const pinyin = CHARS_DB[char]?.pinyin ?? "";
  if (!pinyin) return "";
  return pinyin[0].toLowerCase();
}

/**
 * 五音位阶分：取名字末字声母归宫商角徵羽
 *  宫=100 商=85 角=70 徵=55 羽=40
 *  若羽克宫：判逆乱，分数 −30
 */
function pitchScore(lastChar: string, dominant: Element): { score: number; note: string } {
  const sm = getPinyinFirstChar(lastChar);
  const gong = SHENG_MU_TO_GONG[sm] ?? "宫";
  let base = GONG_RANK[gong];
  // 羽克宫（火→水）：羽属水，宫属土，土克水正常；若羽水克宫土，仅在 羽水克 宫火（误配） 时算逆乱
  if (gong === "宫" && dominant === "水") {
    return { score: Math.max(0, base - 30), note: "物犯君位（羽水冲宫土）" };
  }
  return { score: base, note: `末字归${gong}音（${gong === "宫" ? "君" : gong === "商" ? "臣" : gong === "角" ? "民" : gong === "徵" ? "事" : "物"}位）` };
}

// =====================================================================
// ===                    维度三 · 三才生克（20%）                    ===
// =====================================================================

/** 五行相生：金→水→木→火→土→金 */
const SHENG: Record<Element, Element> = {
  金: "水", 水: "木", 木: "火", 火: "土", 土: "金",
};
/** 五行相克：金→木→木→土→土→水→水→火→火→金（隔一个） */
const KE: Record<Element, Element> = {
  金: "木", 木: "土", 土: "水", 水: "火", 火: "金",
};

/**
 * 三才生克分：
 *  - 顺生链（天格生人格，人格生地格）：100
 *  - 顺生一环 + 比和一环：85
 *  - 比和：65
 *  - 一逆一顺：40
 *  - 逆克链（地克人，人克天）：0，并额外扣 20
 */
function sanCaiScore(
  tianEl: Element, renEl: Element, diEl: Element
): { score: number; note: string } {
  // 顺生链
  if (SHENG[tianEl] === renEl && SHENG[renEl] === diEl) {
    return { score: 100, note: "三才顺生链，天人地流通无碍" };
  }
  // 一顺一比和
  if ((SHENG[tianEl] === renEl && renEl === diEl) || (tianEl === renEl && SHENG[renEl] === diEl)) {
    return { score: 88, note: "顺生一环，母子相扶" };
  }
  // 全比和
  if (tianEl === renEl && renEl === diEl) {
    return { score: 65, note: "三才比和，母得子为扶，吉" };
  }
  // 一逆一顺（地克人但人生天 / 人克天但天生人）
  if (KE[diEl] === renEl && SHENG[renEl] === tianEl) {
    return { score: 45, note: "地克人、人生天，先逆后顺，化凶为财" };
  }
  if (KE[renEl] === tianEl && SHENG[diEl] === renEl) {
    return { score: 45, note: "人克天、地生人，反局流转" };
  }
  // 逆克链
  if (KE[diEl] === renEl && KE[renEl] === tianEl) {
    return { score: 0, note: "三才逆克链，下犯上，困顿之象" };
  }
  // 其他杂局
  return { score: 50, note: "三才杂局，气运流转不明" };
}

// =====================================================================
// ===                    维度四 · 刑冲破害（罚10%）                  ===
// =====================================================================

/**
 * 简化：按总笔画末位映射到常见"凶"组合
 *  - 笔画和 % 2 偶数（阴极）：潜在刑
 *  - 笔画和 % 3 余 0：三刑之象
 *  - 笔画和 % 6 余 0：六冲之象
 *  - 笔画和 % 6 余 3：六害之象
 * 同时检查"六合"（如姓名含 寅+亥 / 午+未 等组合）→ 此处简化
 */
function penaltyScore(charStrokes: number[], hasAllElements: boolean, _counts: Record<Element, number>): { score: number; note: string } {
  const total = charStrokes.reduce((s, n) => s + n, 0);
  let deduct = 0;
  const notes: string[] = [];
  if (total % 2 === 0) {
    deduct += 1.5;
    notes.push("阴极偶数，潜在暗刑");
  }
  if (total % 3 === 0) {
    deduct += 2;
    notes.push("三刑之象，气机受阻");
  }
  if (total % 6 === 0) {
    deduct += 4;
    notes.push("六冲之象，气场对撞");
  } else if (total % 6 === 3) {
    deduct += 2;
    notes.push("六害之象，暗耗元气");
  }
  // 五行俱全（德合护身）抵消 30%
  if (hasAllElements) {
    deduct = Math.max(0, deduct * 0.7);
    notes.push("然五行俱全，德合护身，刑冲可化");
  }
  return { score: Math.min(10, deduct), note: notes.join("；") };
}

// =====================================================================
// ===                        纳音 + 判词                              ===
// =====================================================================

const NAYIN: Record<Element, string[]> = {
  金: ["海中金", "剑锋金", "金箔金", "沙中金", "白蜡金"],
  木: ["大林木", "杨柳木", "松柏木", "平地木", "桑柘木"],
  水: ["涧下水", "泉中水", "长流水", "天河水", "大海水"],
  火: ["炉中火", "山头火", "霹雳火", "山下火", "覆灯火"],
  土: ["路旁土", "城头土", "屋上土", "大驿土", "沙中土"],
};

const DE_VERSE: Record<Element, string> = {
  金: "金主义，肃杀以成；刚毅果断，权衡轻重",
  木: "木性仁，曲直可绳；生发向上，担当有成",
  水: "水主智，润下以顺；机敏深邃，智慧流通",
  火: "火主礼，炎上以明；炽烈爆发，热情感染",
  土: "土德信，稼穑以成；厚重沉稳，承载四方",
};

/**
 * 阶位阈值（已放宽，让"中上品/中品/上品"有更多条目能展示）
 *  - 上品: >= 70（原 80）
 *  - 中上品: >= 55（原 60）
 *  - 中品: >= 38（原 40）
 *  - 中下品: >= 20
 *  - 下品: >= 0
 */
const RANK_TITLES = [
  { min: 70, title: "太和天极", level: "上品" },
  { min: 55, title: "经纬济世", level: "中上品" },
  { min: 38, title: "文质怀才", level: "中品" },
  { min: 20, title: "乘势进退", level: "中下品" },
  { min: 0, title: "孤渊未开", level: "下品" },
] as const;

// =====================================================================
// ===                            主入口                              ===
// =====================================================================

export interface Fortune {
  /** 0-100 综合分 */
  total: number;
  /** 五等阶位：上品 / 中上品 / 中品 / 中下品 / 下品 */
  rank: typeof RANK_TITLES[number]["level"];
  /** 称号 */
  title: typeof RANK_TITLES[number]["title"];
  /** 纳音标签（取该主导元素下首字末字组合） */
  nayin: string;
  /** 一句话判词（基于主导元素） */
  verse: string;
  /** 四维明细 */
  breakdown: {
    virtue: { score: number; note: string };
    pitch: { score: number; note: string };
    sanCai: { score: number; note: string };
    penalty: { score: number; note: string };
  };
}

export function judgeFortune(
  raw: string,
  counts: Record<Element, number>,
  dominant: Element,
  charStrokes: number[]
): Fortune {
  // 维度 1：德性
  const virtue = virtueScore(counts);

  // 维度 2：音阶（取末字声母）
  const lastChar = Array.from(raw).filter((c) => /[\u4e00-\u9fa5]/.test(c)).pop() ?? "";
  const pitch = pitchScore(lastChar, dominant);

  // 维度 3：三才（姓氏天格 = 主导，名字首字人格，名字末字地格）
  const chars = Array.from(raw).filter((c) => /[\u4e00-\u9fa5]/.test(c));
  const tianEl: Element = dominant; // 姓氏 / 主导
  const renEl: Element = counts["木"] > 0 ? "木" : (counts["火"] > 0 ? "火" : dominant); // 名字首字
  const diEl: Element = lastChar && counts[dominant] > 0 ? dominant : SHENG[dominant];
  const sanCai = sanCaiScore(tianEl, renEl, diEl);

  // 维度 4：刑冲罚分
  const hasAllElements = ELEMENTS.every((el) => counts[el] > 0);
  const penalty = penaltyScore(charStrokes, hasAllElements, counts);

  // 综合分
  const total = Math.max(
    0,
    Math.min(
      100,
      virtue.score * 0.4 + pitch.score * 0.3 + sanCai.score * 0.2 - penalty.score
    )
  );

  // 阶位
  const rankInfo = RANK_TITLES.find((r) => total >= r.min) ?? RANK_TITLES[RANK_TITLES.length - 1];

  // 纳音（按主导元素 + 末字笔画 % 5 选一）
  const lastStrokes = charStrokes[charStrokes.length - 1] ?? 0;
  const nayinList = NAYIN[dominant];
  const nayin = nayinList[lastStrokes % nayinList.length];

  return {
    total: Math.round(total),
    rank: rankInfo.level,
    title: rankInfo.title,
    nayin,
    verse: DE_VERSE[dominant],
    breakdown: { virtue, pitch, sanCai, penalty },
  };
}
